type ItemId = string;

type Identifiable = {
  id: ItemId;
};

export type SelectionState<T extends Identifiable = Identifiable> = {
  items: T[]; // all items in visual order
  itemIds: Set<ItemId>; // IDs of all items (for efficient lookups)
  selected: Set<ItemId>; // IDs of selected items
  anchor: ItemId | null; // range anchor (last non-Shift click / cmd-click)
  lastClicked: ItemId | null; // last clicked item
};

export type SelectionAction<T extends Identifiable = Identifiable> =
  | { type: 'setItems'; items: T[] }
  | { type: 'toggleSingle'; id: ItemId } // ⌘-click - toggle this item in/out of selection
  | { type: 'extendRangeTo'; id: ItemId } // Shift-click - select continuous range from anchor to target
  | { type: 'selectAll' } // Select all items (cmd-a)
  | { type: 'clearSelection' }; // Clear selection

export const initialSelectionState = <T extends Identifiable = Identifiable>(items: T[] = []): SelectionState<T> => ({
  items,
  itemIds: new Set(items.map((item) => item.id)),
  selected: new Set<ItemId>(),
  anchor: null,
  lastClicked: null,
});

/* ---------------- reducer ---------------- */

export function selectionReducer<T extends Identifiable = Identifiable>(
  state: SelectionState<T>,
  action: SelectionAction<T>
): SelectionState<T> {
  switch (action.type) {
    case 'setItems': {
      const items = action.items;

      // Early return if items array hasn't changed (prevents infinite loops)
      if (items.length === state.items.length && items.every((item, idx) => state.items[idx]?.id === item.id)) {
        return state;
      }

      const itemIds = new Set(items.map((item) => item.id));
      const selected = new Set(Array.from(state.selected).filter((id) => itemIds.has(id)));
      const anchor = state.anchor && itemIds.has(state.anchor) ? state.anchor : null;
      const lastClicked = state.lastClicked && itemIds.has(state.lastClicked) ? state.lastClicked : null;
      return { ...state, items, selected, anchor, itemIds, lastClicked };
    }

    case 'toggleSingle': {
      const id = action.id;
      if (!state.itemIds.has(id)) return state;
      return {
        ...state,
        selected: toggle(state.selected, id),
        anchor: id,
        lastClicked: id,
      };
    }

    case 'extendRangeTo': {
      const id = action.id;
      if (!state.itemIds.has(id)) return state;

      // Determine the anchor (use clicked item if no anchor exists)
      const anchor = state.anchor ?? id;

      // Calculate the new range from anchor to target
      const newRange = rangeBetween(state.items, anchor, id);

      // Find items that are "islands" - selected items outside the current range
      // (between anchor and lastClicked). These should be preserved.
      const currentRange = state.lastClicked ? rangeBetween(state.items, anchor, state.lastClicked) : [];
      const currentRangeSet = new Set(currentRange);
      const islands = Array.from(state.selected).filter((itemId) => !currentRangeSet.has(itemId));

      // New selection = new range + preserved islands
      const newSelection = union(new Set(newRange), islands);

      return {
        ...state,
        anchor,
        selected: newSelection,
        lastClicked: id,
      };
    }

    case 'selectAll': {
      return {
        ...state,
        selected: state.itemIds,
        anchor: null,
        lastClicked: null,
      };
    }

    case 'clearSelection': {
      return {
        ...state,
        selected: new Set(),
        anchor: null,
        lastClicked: null,
      };
    }

    default:
      return state;
  }
}

/* ---------------- helpers ---------------- */

const indexOf = <T extends Identifiable>(items: T[], id: ItemId) => items.findIndex((item) => item.id === id);

const rangeBetween = <T extends Identifiable>(items: T[], a: ItemId, b: ItemId): ItemId[] => {
  const ia = indexOf(items, a);
  const ib = indexOf(items, b);
  if (ia === -1 || ib === -1) return [];
  const [start, end] = ia <= ib ? [ia, ib] : [ib, ia];
  return items.slice(start, end + 1).map((item) => item.id);
};

const toggle = (set: Set<ItemId>, id: ItemId) => {
  const next = new Set(set);
  next.has(id) ? next.delete(id) : next.add(id);
  return next;
};

const union = (a: Set<ItemId>, b: Iterable<ItemId>) => {
  const out = new Set(a);
  Array.from(b).forEach((x) => out.add(x));
  return out;
};
